import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { competitionApi, type CompetitionInfo } from '@/api/client';

/****************
 * THE $1,000 PAPER-TRADING COMPETITION, ON THE WEB. 2026-10-25, rules of 10-28.
 *
 * A competition session is a 4th destination in Go Live step 3, beside Paper,
 * Telegram and a broker key: locked until the competition opens, on the
 * account's competition wallet (10,000 USDT, then whatever the last session
 * ended with), one at a time, any trader, group, category, strategy or AI, no
 * leverage cap. The server owns every rule (backend store/competition*.go);
 * this file only reads them.
 ****************/

/** Opens the competition video from anywhere (the chooser's orange answer). */
export const COMPETITION_VIDEO_EVENT = 'copyfi:competition-video';
export type CompetitionVideoKind = 'long' | 'short';
export const openCompetitionVideo = (kind: CompetitionVideoKind = 'long') =>
  window.dispatchEvent(new CustomEvent(COMPETITION_VIDEO_EVENT, { detail: { kind } }));

/** Mishka's two videos (2026-10-26): long after the chooser, short on the competition page. */
export const COMPETITION_VIDEOS: Record<CompetitionVideoKind, { src: string; poster: string }> = {
  long: { src: '/videos/copyfi-competition-long.mp4', poster: '/videos/copyfi-competition-long-poster.jpg' },
  short: { src: '/videos/copyfi-competition-short.mp4', poster: '/videos/copyfi-competition-short-poster.jpg' },
};

/* The short video opens by itself once: the first time the competition page
   is opened, unless a competition video was already watched. */
const INTRO_KEY = 'copyfi.competitionIntroSeen';
export function competitionIntroSeen(): boolean {
  try { return localStorage.getItem(INTRO_KEY) === '1'; } catch { return true; }
}
export function markCompetitionIntroSeen(): void {
  try { localStorage.setItem(INTRO_KEY, '1'); } catch { /* private mode */ }
}

/** Go Live step 3's competition destination (beside paper, Telegram, a key). */
export const COMPETITION_DESTINATION = '__competition__';

/** Where the competition session is started: Go Live with Competition chosen and held. */
export const COMPETITION_START_PATH = '/copy-trade?competition=1';

/** Why the Competition destination cannot be chosen now ('' = it can). */
export function competitionDestinationBlock(info: CompetitionInfo | undefined): '' | 'loading' | 'upcoming' | 'running' | 'ended' | 'disqualified' {
  if (!info) return 'loading';
  if (info.entry?.disqualified) return 'disqualified';
  if (info.can_enter) return '';
  if (info.phase === 'upcoming') return 'upcoming';
  if (info.phase === 'ended') return 'ended';
  return info.running ? 'running' : 'upcoming';
}

export interface TimeLeft { days: number; hours: number; minutes: number; seconds: number; done: boolean }

/** What is left until `target`, never negative. */
export function timeLeft(target: string | number | Date, now: number = Date.now()): TimeLeft {
  const end = new Date(target).getTime();
  let ms = Number.isFinite(end) ? Math.max(0, end - now) : 0;
  const days = Math.floor(ms / 86_400_000); ms -= days * 86_400_000;
  const hours = Math.floor(ms / 3_600_000); ms -= hours * 3_600_000;
  const minutes = Math.floor(ms / 60_000); ms -= minutes * 60_000;
  const seconds = Math.floor(ms / 1000);
  return { days, hours, minutes, seconds, done: days + hours + minutes + seconds === 0 };
}

/** The moment the countdown counts to: the start while upcoming, else the end. */
export function countdownTarget(info: Pick<CompetitionInfo, 'phase' | 'starts_at' | 'ends_at'>): string {
  return info.phase === 'upcoming' ? info.starts_at : info.ends_at;
}

/** A clock that ticks every `every` ms, for a countdown. */
export function useNow(every = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), every);
    return () => window.clearInterval(id);
  }, [every]);
  return now;
}

/** The competition as the server sees it for this account. */
export function useCompetition(enabled = true) {
  return useQuery({
    queryKey: ['competition'],
    queryFn: competitionApi.info,
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

/** Whether a session is one of this account's competition sessions. */
export function isCompetitionSession(info: CompetitionInfo | undefined, sessionId: number | string | undefined): boolean {
  if (!info || sessionId === undefined) return false;
  return competitionSessionIds(info).has(Number(sessionId));
}

/** A YouTube or Vimeo page link, made playable in a frame; anything else as given. */
export function embeddableVideo(url: string): { kind: 'frame' | 'file'; src: string } | null {
  const u = url.trim();
  if (!u) return null;
  const yt = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (yt) return { kind: 'frame', src: `https://www.youtube.com/embed/${yt[1]}` };
  const vimeo = u.match(/vimeo\.com\/(\d+)/);
  if (vimeo) return { kind: 'frame', src: `https://player.vimeo.com/video/${vimeo[1]}` };
  return { kind: 'file', src: u };
}

/** The rules' numbers, for screens drawn before the server answers. The server enforces them. */
export const COMPETITION_WALLET = 10_000;

/** Who sees the competition: everyone, before it opens too, so they can register (Guillaume, 10-28). */
export function competitionVisible(info: Pick<CompetitionInfo, 'is_admin' | 'phase'> | undefined): boolean {
  return !!info;
}

/** The account's competition session id, frozen or not (0 when none). */
export function competitionSessionId(info: CompetitionInfo | undefined): number {
  return info?.entry?.session_id ?? 0;
}

/** Every competition session of the account (an admin may have several). */
export function competitionSessionIds(info: CompetitionInfo | undefined): Set<number> {
  const ids = new Set<number>(info?.session_ids ?? []);
  if (info?.entry?.session_id) ids.add(info.entry.session_id);
  return ids;
}
